import React, { useState, useCallback, useEffect } from 'react';
import { kpiApi } from '../../services/api';
import { useDbNavigate } from '../../context/DatabaseContext';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
  Network,
  Wifi,
  Cable,
  Server,
  Layers,
  MessageSquare
} from 'lucide-react';
import DashboardLayout from '../../components/common/DashboardLayout';
import DashboardTile from '../../components/common/DashboardTile';
import { PieTooltip } from '../../components/common/ChartTooltips';

/**
 * NetworkingDashboardPage - KPI overview for networking metrics
 */
const NetworkingDashboardPage = () => {
  const navigate = useDbNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kpis, setKpis] = useState(null);

  const loadKpis = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await kpiApi.getNetworking();
      setKpis(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load KPIs');
    } finally {
      setLoading(false);
    }
  }, []);

  // Re-fetch when database selection changes
  useEffect(() => {
    const handler = () => loadKpis();
    window.addEventListener('pmd-database-changed', handler);
    return () => window.removeEventListener('pmd-database-changed', handler);
  }, [loadKpis]);

  // Status colors (for Recharts Cell fill)
  const statusColors = {
    'Active': '#22c55e',
    'Spare': '#94a3b8',
    'Pending Connection': '#f59e0b',
    'Reserved': '#3b82f6',
    'Future': '#8b5cf6',
    'Inactive': '#ef4444',
    'De-Activated': '#ef4444',
    'Unknown': '#e2e8f0'
  };

  const getStatusColor = (status) => statusColors[status] || statusColors['Unknown'];

  // Status classes (for legend dots / tooltips — no inline styles)
  const statusClasses = {
    'Active': 'stui-c-green',
    'Spare': 'stui-c-slate',
    'Pending Connection': 'stui-c-amber',
    'Reserved': 'stui-c-blue',
    'Future': 'stui-c-violet',
    'Inactive': 'stui-c-red',
    'De-Activated': 'stui-c-red',
    'Unknown': 'stui-c-gray'
  };

  const getStatusClass = (status) => statusClasses[status] || 'stui-c-gray';

  const palette = [
    '#3b82f6','#22c55e','#f59e0b','#ef4444','#8b5cf6',
    '#06b6d4','#f97316','#10b981','#6366f1','#ec4899',
    '#14b8a6','#84cc16','#a855f7','#0ea5e9','#d946ef'
  ];
  const paletteClasses = [
    'stui-c-blue','stui-c-green','stui-c-amber','stui-c-red','stui-c-violet',
    'stui-c-cyan','stui-c-orange','stui-c-emerald-light','stui-c-indigo','stui-c-pink',
    'stui-c-teal','stui-c-lime','stui-c-purple','stui-c-sky','stui-c-fuchsia'
  ];
  const getPaletteColor = (_, i) => palette[i % palette.length];
  const getPaletteClass = (_, i) => paletteClasses[i % paletteClasses.length];

  // Convert API data to recharts format
  const toChartData = (grouped, colorFn = getStatusColor, classFn = getStatusClass) => {
    if (!grouped || grouped.length === 0) return [];
    return grouped.filter(g => g.count > 0).map((g, i) => ({
      name: g.key,
      value: g.count,
      fill: colorFn(g.key, i),
      colorClass: classFn(g.key, i)
    }));
  };

  // Network section content (rendered inside DashboardTile)
  const NetworkContent = ({ total, distinctLabel, distinctValue, extraLabel, extraValue, statusData, colorFn, classFn }) => {
    const chartData = toChartData(statusData, colorFn, classFn);
    const totalRecords = chartData.reduce((sum, d) => sum + d.value, 0);

    return (
      <div className="stui-dashboard-visual-section-content">
        <div className="stui-dashboard-visual-stats">
          <div className="stui-dashboard-visual-stat">
            <span className="stui-dashboard-visual-total-value">{total}</span>
            <span className="stui-dashboard-visual-stat-label">Total Records</span>
          </div>
          <div className="stui-dashboard-visual-stat">
            <span className="stui-dashboard-visual-total-value">{distinctValue}</span>
            <span className="stui-dashboard-visual-stat-label">{distinctLabel}</span>
          </div>
          {extraLabel && (
            <div className="stui-dashboard-visual-stat">
              <span className="stui-dashboard-visual-total-value">{extraValue}</span>
              <span className="stui-dashboard-visual-stat-label">{extraLabel}</span>
            </div>
          )}
        </div>

        <div className="stui-dashboard-visual-chart-area">
          {chartData.length > 0 ? (
            <ResponsiveContainer width={140} height={140}>
              <PieChart>
                <Pie
                  data={chartData}
                  dataKey="value"
                  cx="50%"
                  cy="50%"
                  innerRadius={38}
                  outerRadius={62}
                  paddingAngle={1}
                  animationDuration={600}
                >
                  {chartData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} stroke="none" />
                  ))}
                </Pie>
                <Tooltip content={<PieTooltip />} />
                <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" className="stui-dashboard-visual-donut-label">
                  {totalRecords.toLocaleString()}
                </text>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="stui-dashboard-visual-donut stui-empty stui-lg">
              <span className="stui-dashboard-visual-donut-center">0</span>
            </div>
          )}
        </div>

        <div className="stui-dashboard-visual-legend">
          {chartData.map((d, i) => (
            <span key={i} className="stui-legend-item">
              <span className={`stui-legend-dot ${d.colorClass || 'stui-c-slate'}`} />
              <span className="stui-dashboard-visual-legend-key">{d.name}</span>
              <span className="stui-dashboard-visual-legend-count">{d.value.toLocaleString()}</span>
            </span>
          ))}
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout
      id="networking"
      title="Networking Dashboard"
      icon={Network}
      onRefresh={loadKpis}
      loading={loading}
      error={error}
    >
      {/* Row 1: Summary Bar - full width */}
      <DashboardTile colSpan={6} className="stui-dashboard-visual-summary-bar-tile">
        <div className="stui-dashboard-visual-summary-bar">
          <div className="stui-dashboard-visual-summary-stat">
            <Wifi size={18} />
            <span className="stui-dashboard-visual-summary-value">{((kpis?.slnTotal || 0) + (kpis?.dlnTotal || 0)).toLocaleString()}</span>
            <span className="stui-dashboard-visual-summary-label">IP Addresses</span>
          </div>
          <div className="stui-dashboard-visual-summary-stat">
            <Layers size={18} />
            <span className="stui-dashboard-visual-summary-value">{(kpis?.slnSubnets || 0) + (kpis?.dlnSubnets || 0)}</span>
            <span className="stui-dashboard-visual-summary-label">Subnets</span>
          </div>
          <div className="stui-dashboard-visual-summary-stat">
            <Cable size={18} />
            <span className="stui-dashboard-visual-summary-value">{(kpis?.serialTotal || 0).toLocaleString()}</span>
            <span className="stui-dashboard-visual-summary-label">Serial Connections</span>
          </div>
          <div className="stui-dashboard-visual-summary-stat">
            <Server size={18} />
            <span className="stui-dashboard-visual-summary-value">{kpis?.serialNetworks || 0}</span>
            <span className="stui-dashboard-visual-summary-label">Networks</span>
          </div>
        </div>
      </DashboardTile>

      {/* Row 2: Three network tiles */}
      <DashboardTile
        title="Site Level Network (SLN)"
        icon={Wifi}
        onClick={() => navigate('/ip-address-sln')}
      >
        <NetworkContent
          total={kpis?.slnTotal || 0}
          distinctLabel="Subnets"
          distinctValue={kpis?.slnSubnets || 0}
          statusData={kpis?.slnByStatus}
        />
      </DashboardTile>

      <DashboardTile
        title="Device Level Network (DLN)"
        icon={Server}
        onClick={() => navigate('/ip-address-dln')}
      >
        <NetworkContent
          total={kpis?.dlnTotal || 0}
          distinctLabel="Subnets"
          distinctValue={kpis?.dlnSubnets || 0}
          statusData={kpis?.dlnByStatus}
        />
      </DashboardTile>

      <DashboardTile
        title="Serial Networks"
        icon={Cable}
        onClick={() => navigate('/networks-serial')}
      >
        <NetworkContent
          total={kpis?.serialTotal || 0}
          distinctLabel="Networks"
          distinctValue={kpis?.serialNetworks || 0}
          extraLabel="Parent Devices"
          extraValue={kpis?.serialParents || 0}
          statusData={kpis?.serialByStatus}
        />
      </DashboardTile>

      <DashboardTile
        title="PLC Messaging"
        icon={MessageSquare}
        onClick={() => navigate('/plc-msg-map')}
      >
        <NetworkContent
          total={kpis?.plcMsgTotal || 0}
          distinctLabel="Producers"
          distinctValue={kpis?.plcMsgProducers || 0}
          statusData={kpis?.plcMsgByType}
          colorFn={getPaletteColor}
          classFn={getPaletteClass}
        />
      </DashboardTile>
    </DashboardLayout>
  );
};

export default NetworkingDashboardPage;
